import { useNavigate } from 'react-router-dom';
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react';

import Button from '~/shared/components/ButtonsV2';
import Tag from '~/shared/components/Tag';
import Typography from '~/shared/components/Typography';
import LinkIcon from '~/shared/assets/icons/link-gray.svg';
import MoreIcon from '~/shared/assets/icons/more.svg';
import CheckIcon from '~/shared/assets/icons/check.svg';
import CancelIcon from '~/shared/assets/icons/cancel.svg';
import ExceptionIcon from '~/shared/assets/icons/exception-2.svg';
import { formatBytesWithValueAndUnit } from '~/shared/utils/formatNumber';
import { isSwitchedOn } from '~/shared/utils/volumeHelpers';

import type { Volume } from '~/shared/types/api';

interface VolumeInfoCardProps {
  volume: Volume;
  onCreateException?: (volumeId: string) => void;
  onRemoveException?: (volumeId: string, volumeName?: string) => void;
  volumeType?: 'matching' | 'exception' | 'potential';
}

export default function VolumeInfoCard({
  volume,
  onCreateException,
  onRemoveException,
  volumeType,
}: VolumeInfoCardProps) {
  const navigate = useNavigate();
  const volumeId = volume.volumeId ?? 'N/A';
  const utilization = volume.utilization || volume.lastUtilizationData?.datafyDiskUsedPercentage;

  const { value, unit } = formatBytesWithValueAndUnit(volume.diskSize || 0, 1);

  const isAutoscalingOn = isSwitchedOn(volume);

  return (
    <div className="flex flex-col gap-2  p-5 h-fit overflow-y-auto border-gray-300 border rounded-lg">
      <div className="flex justify-between items-center">
        <Typography variant="p2-semi" color="text-gray-500" fontWeight={500} className="">
          {volumeId}
        </Typography>
        <div className="flex">
          <Button
            variant="text"
            size="sm"
            className="group w-7 !h-7 aspect-square"
            icon={
              <LinkIcon className="size-4 text-gray-600 group-hover:stroke-primary-500 group-hover:text-primary-500" />
            }
            onClick={() => navigate(`/fleet-manager/${volumeId}`)}
          />

          {volumeType !== 'exception' && (
            <Menu as="div" className="relative">
              <MenuButton
                as={Button}
                variant="text"
                size="sm"
                className="w-7 !h-7 aspect-square"
                icon={<MoreIcon className="size-4 text-gray-600" />}
              />
              <Transition
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <MenuItems
                  className="absolute right-0 mt-2 w-60 origin-top-right rounded-lg bg-white border border-gray-300 shadow-lg focus:outline-none z-50"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MenuItem>
                    {({ active }) => (
                      <button
                        onClick={() => onCreateException?.(volumeId)}
                        className={`${
                          active ? 'bg-primary-20' : ''
                        } group flex  w-full rounded-lg items-center px-3 py-3 text-sm text-center cursor-pointer bg-re`}
                      >
                        <div>
                          <ExceptionIcon className="size-5  text-gray-600" />
                        </div>
                        <Typography
                          variant="p3-reg"
                          color=" text-primary-900 text-nowrap text-center mx-auto"
                        >
                          Create exception from rules
                        </Typography>
                      </button>
                    )}
                  </MenuItem>
                </MenuItems>
              </Transition>
            </Menu>
          )}
        </div>
      </div>
      <div className="min-h-[28px]">
        {volume.instanceId && <Tag variant="instance" size="sm" text={volume.instanceId} />}
      </div>

      <div className="flex flex-col gap-1.5">
        <div className="flex divide-x-2 gap-2 py-1 divide-gray-400 ">
          <div className="flex gap-1 items-center pr-3">
            <Typography variant="p3-reg" color="text-gray-800" fontWeight={400} className="">
              Autoscaling
            </Typography>
            {isAutoscalingOn ? (
              <CheckIcon className="size-[14px] mx-1 text-green stroke-0.5" />
            ) : (
              <CancelIcon className="size-[20px] text-red" />
            )}
          </div>
          {utilization && utilization > 0 && (
            <Typography variant="p3-reg" color="text-gray-800" fontWeight={400} className="pr-3">
              {Math.round(utilization)}% utilized
            </Typography>
          )}
          <Typography variant="p3-reg" color="text-gray-800" fontWeight={400} className="pr-3">
            {value} {unit}
          </Typography>
        </div>
      </div>

      {volumeType === 'exception' && (
        <div className="flex items-center">
          <ExceptionIcon className="size-6 mr-1 text-gray-600" />
          <Typography variant="p3-reg" color="text-gray-800" fontWeight={400} className="">
            Manual overide
          </Typography>
          <Button
            variant="third"
            size="sm"
            className="ml-auto"
            onClick={() => onRemoveException?.(volumeId, volume.volumeId)}
          >
            <Typography variant="buttons-m" color="text-primary-900" fontWeight={600} className="">
              Remove Exception
            </Typography>
          </Button>
        </div>
      )}
    </div>
  );
}
